/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    Button,
    ITelemetryContent,
    getPayloadObject,
    getTelemetryAttributes
} from '@msdyn365-commerce-modules/utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import React from 'react';

import classnames from 'classnames';

/**
 * Class names used in component.
 */
const componentClassName = 'msc-search-bar';
const filterOptionsClassName = `${componentClassName}__search-options`;
const filterOptionClassName = `${componentClassName}__search-option`;
const filterOptionSelectedClassName = `${componentClassName}__search-option-selected`;
const searchBarClassName = `${componentClassName}__search-text`;
const buttonClassName = `${componentClassName}__search-button`;

/**
 * ISearchBarComponent interface.
 */
interface ISearchBarComponent extends IComponent<ISearchBarProps> {}

/**
 * ISearchBarResources interface.
 */
export interface ISearchBarResources {
    searchOptionsAriaLabel?: string;
    searchBarPlaceholder?: string;
    searchBarAriaLabel?: string;
    searchButtonLabel?: string;
    searchButtonAriaLabel?: string;
}

/**
 * ISearchBarData interface.
 */
export interface ISearchBarData {
    options: ISearchBarOption[];
}

/**
 * ISearchBarOption interface.
 */
export interface ISearchBarOption {
    label: string;
    value: string;
}

/**
 * ISearchBarProps interface.
 */
export interface ISearchBarProps extends IComponentProps<ISearchBarData> {
    className?: string;
    resources?: ISearchBarResources;
    currentSearchOption?: ISearchBarOption;
    currentSearchText?: string;
    telemetryContent?: ITelemetryContent;
    clearSearchTextOnOptionChange?: boolean;
    onSelectOption?(newSearchOption?: ISearchBarOption): void;
    onChange?(searchOption?: ISearchBarOption, searchText?: string): void;
    onBlur?(searchOption?: ISearchBarOption, searchText?: string): void;
    onClick?(searchOption?: ISearchBarOption, searchText?: string): void;
}

/**
 * SearchBar component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const SearchBarFunctionalComponent: React.FC<ISearchBarProps> = (props: ISearchBarProps) => {
    const [searchOptionState, setSearchOptionState] = React.useState(props.currentSearchOption);
    const [searchTextState, setSearchTextState] = React.useState(props.currentSearchText);

    const onSelectOptionHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        const searchOption = props.data.options?.find((option: ISearchBarOption) => option.value === value);
        setSearchOptionState(searchOption);
        if (props.clearSearchTextOnOptionChange) {
            setSearchTextState('');
        }
        props.onSelectOption?.(searchOption);
    };

    const onChangeHandler = (event: React.FocusEvent<HTMLInputElement>): void => {
        const value = event.currentTarget.value;
        setSearchTextState(value);
        props.onChange?.(searchOptionState, value);
    };

    const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setSearchTextState(value);
        props.onBlur?.(searchOptionState, value);
    };

    const onClickHandler = () => {
        props.onClick?.(searchOptionState, searchTextState);
    };

    // define telemetry attributes
    const searchButtonAttributes = getTelemetryAttributes(
        props.telemetryContent!,
        getPayloadObject('click', props.telemetryContent!, "Search")
    );

    return (
        <div className={classnames(props.className, componentClassName)}>
            {ArrayExtensions.hasElements(props.data.options) && (
                <select
                    className={filterOptionsClassName}
                    title={props.resources?.searchOptionsAriaLabel}
                    aria-label={props.resources?.searchOptionsAriaLabel}
                    onChange={onSelectOptionHandler}
                >
                    {props.data.options?.map((option: ISearchBarOption) => {
                        return (
                            <option
                                key={option.value}
                                value={option.value}
                                aria-label={option.label}
                                aria-selected={option.value === searchOptionState?.value}
                                className={classnames(
                                    filterOptionClassName,
                                    option.value === searchOptionState?.value ? filterOptionSelectedClassName : ''
                                )}
                            >
                                {option.label}
                            </option>
                        );
                    })}
                </select>
            )}
            <input
                type='text'
                className={classnames(searchBarClassName, ArrayExtensions.hasElements(props.data.options) ? '' : 'full-width')}
                placeholder={props.resources?.searchBarPlaceholder}
                title={props.resources?.searchBarAriaLabel}
                aria-label={props.resources?.searchBarAriaLabel}
                value={searchTextState}
                onChange={onChangeHandler}
                onBlur={onBlurHandler}
            />
            <Button
                className={buttonClassName}
                aria-label={props.resources?.searchButtonAriaLabel ?? props.resources?.searchButtonLabel}
                title={props.resources?.searchButtonAriaLabel ?? props.resources?.searchButtonLabel}
                onClick={onClickHandler}
                {...searchButtonAttributes}
            >
                {props.resources?.searchButtonLabel}
            </Button>
        </div>
    );
};

/**
 * SearchBar component.
 */
export const SearchBarComponent: React.FunctionComponent<ISearchBarProps> = msdyn365Commerce.createComponent<ISearchBarComponent>(
    'SearchBarComponent',
    { component: SearchBarFunctionalComponent }
);
